<template>
    <transition name="fade">
        <div v-if="isApplePaySupported" id="nspay-apple-pay" @click="processApplePayment">
            <div id="apple-pay-logo"></div>
        </div>
    </transition>
</template>

<script async src="https://js.braintreegateway.com/web/3.63.0/js/apple-pay.min.js">
    /*
     * ApplePay JS API that contains ApplePaySession object.
     * ApplePaySession is the entry point for Apple Pay on the web.
     * All the steps of the payment process for a single transaction occur in a session.
     * ApplePaySession belongs to the Apple Pay JS API.
     */
</script>

<script>
    import braintree from "braintree-web";
    import axios from "axios";
    import { mapGetters, mapMutations, mapState } from "vuex";
    import { sweetAlert2 } from "../../../mixins/sweet-alert2";
    import { showBlocker } from "../../../mixins/show-blocker";
    import { paymentMethods } from "../../../constants/payment-methods";
    import { appSettings } from "../../../mixins/app-settings";

    // This supports iOS 10 and macOS 10.12, latest is version 3 but support starts at iOS/macOS 13
    const supportedApplePayVersion = 1;

    let paymentRequest = null;
    let applePaySession = null;
    let applePayClient = undefined;

    export default {
        name: "ApplePay",
        mixins: [sweetAlert2, showBlocker, appSettings],
        data: function () {
            return {
                applePayInitialized: false
            }
        },
        computed: {
            ...mapState({                
                appName: state => state.session.appName,
                siteId: state => state.session.siteId,
                accountId: state => state.session.accountId,
                checkId: state => state.session.checkId,
                tip: state => state.session.tip
            }),
            ...mapGetters([
                "balanceWithTip"
            ]),
            isApplePaySupported() {
                return window.ApplePaySession // Device supports ApplePay (iOS 10+ or macOS 10.12+)
                    && ApplePaySession.supportsVersion(supportedApplePayVersion)
                    && ApplePaySession.canMakePayments() // Device can make payments with ApplePay (Region/Country is supported: https://support.apple.com/en-us/HT207957)
            }
        },
        methods: {
            ...mapMutations([
                "setLoadingStatus",
                "setPayment"
            ]),
            async createApplePayRequest() {
                const tokenUrl = this.getApiBaseUrl() + "/payment/clientToken/" + this.siteId + "/" + this.accountId;
                const siteNameUrl = this.getApiBaseUrl() + "/site/sitename/" + this.siteId + "/" + this.accountId;
                let payeeName = this.appName;
                let hasExited = false;

                let clientToken = await axios.all([
                    axios.get(tokenUrl),
                    axios.get(siteNameUrl)
                ])
                    .then(responseArray => {
                        if (responseArray[1].data) {
                            payeeName = responseArray[1].data;
                        }

                        if (responseArray[0].data) {
                            return responseArray[0].data
                        }
                        else {
                            this.$router.push({ name: "Error", params: { errorCode: "1010" } });
                            hasExited = true;
                        }
                    })
                    .catch(error => {
                        if (error.response &&
                            error.response.data &&
                            error.response.data.errorCode) {
                            this.$router.push({ name: "Error", params: { errorCode: error.response.data.errorCode } });
                        }
                        else {
                            if (error.response &&
                                error.response.data &&
                                error.response.data.errorMessage) {
                                console.error(error.response.data.errorMessage)
                            }

                            this.$router.push({ name: "Error", params: { errorCode: "1000" } });
                        }
                        hasExited = true;
                    })

                if (hasExited) {
                    return;
                }

                if (clientToken !== undefined) {
                    // Create the Braintree ApplePay client.
                    applePayClient = await braintree.client.create({
                        authorization: clientToken
                    })
                        .then(braintreeClientInstance => {
                            return braintree.applePay.create({
                                client: braintreeClientInstance
                            })
                        })
                        .then(applePayClientInstance => {
                            return applePayClientInstance;
                        }).catch(error => {
                            console.error(error);
                        });

                    if (applePayClient !== undefined) {
                        paymentRequest = applePayClient.createPaymentRequest({
                            total: {
                                label: payeeName,
                                amount: this.balanceWithTip.toFixed(2).toString()
                            }
                        });
                    }

                    this.applePayInitialized = true;
                }
            },
            processApplePayment() {
                // We don't alert the user if initialization fails because user may not pick
                // Apple Pay as the payment method. Alert user only if it's clicked on.
                if (!this.applePayInitialized) {
                    console.error("Apple Pay failed to initialize but Apple Pay payment triggered.");
                    this.swalError("1013");
                    return;
                }

                applePaySession = new ApplePaySession(supportedApplePayVersion, paymentRequest);
                applePaySession.onvalidatemerchant = this.onValidateMerchant;
                applePaySession.onpaymentauthorized = this.onPaymentAuthorized;
                applePaySession.begin();
            },
            onValidateMerchant(event) {
                applePayClient.performValidation({
                    validationURL: event.validationURL,
                    displayName: "NorthStar Pay",
                },
                    (validateMerchantErr, merchantSession) => {
                        if (validateMerchantErr) {
                            console.err(validateMerchantErr);
                            this.swalError("1020");
                            return;
                        }
                        applePaySession.completeMerchantValidation(merchantSession);
                    });
            },
            onPaymentAuthorized(event) {
                applePayClient.tokenize({
                    token: event.payment.token
                },
                    async (tokenizeErr, payload) => {
                        if (tokenizeErr) {
                            console.error(tokenizeErr);
                            applePaySession.completePayment(ApplePaySession.STATUS_FAILURE);
                            this.$router.push({ name: "Error", params: { errorCode: "1014" } });
                            return;
                        }

                        // After you have transacted with the payload.nonce,
                        // call `completePayment` to dismiss the Apple Pay sheet.
                        applePaySession.completePayment(ApplePaySession.STATUS_SUCCESS);

                        await this.sendPayloadToServer(payload);
                    });
            },
            async sendPayloadToServer(payment) {
                this.startProgressIndicator();

                let payload = {
                    SiteId: this.siteId,
                    AccountId: this.accountId,
                    CheckId: this.checkId,
                    PaymentAmount: this.balanceWithTip,
                    TipAmount: this.tip,
                    PaymentNonce: payment.nonce
                }

                await axios.post(this.getApiBaseUrl() + "/payment/payment", payload)
                    .then(response => {
                        if (response.data) {
                            let payment = response.data;
                            payment.paymentName = paymentMethods.applePay;
                            this.setPayment(payment);
                            this.$router.push({ name: "PaymentSummary" });
                        }
                        else {
                            this.$router.push({ name: "Error", params: { errorCode: "1010" } });
                        }
                    })
                    .catch(error => {
                        if (error.response &&
                            error.response.data &&
                            error.response.data.errorCode) {
                            switch (error.response.data.errorCode) {
                                case "1008":
                                case "1009":
                                    this.$router.push({ name: "Error", params: { errorCode: error.response.data.errorCode } });
                                    break;
                                default:
                                    if (error.response.data.errorMessage) {
                                        console.error(error.response.data.errorMessage);
                                    }
                                    this.$router.push({ name: "Error", params: { errorCode: "1010" } });
                            }
                        }
                        else {
                            console.error(error);
                            this.$router.push({ name: "Error", params: { errorCode: "1010" } });
                        }
                    })
                    .finally(() => {
                        this.stopProgressIndicator();
                    });
            }
        },
        async mounted() {
            await this.createApplePayRequest();
        }
    }
</script>